import { Logger } from '_common/services';
import { BaseTypedEmitter } from '_common/services/Realtime';
import { EditorDOMUtils } from '../_Common/DOM';

class EventsManager extends BaseTypedEmitter<Editor.Events.EventTypes> {
  private static instance: EventsManager;
  private mousedownTarget: EventTarget | null;
  private documentContainer: HTMLElement | undefined;
  private pageListeners: Editor.Events.HTMLElementListenerType;
  private editorRootListeners: Editor.Events.HTMLElementListenerType;
  private documentListeners: Editor.Events.HTMLElementListenerType;

  private DEBUG: boolean = false;

  constructor() {
    super();

    this.mousedownTarget = null;

    this.pageListeners = {
      mouseover: this.handleMouseOver.bind(this),
      mousedown: this.handleMouseDown.bind(this),
      mouseup: this.handleMouseUp.bind(this),
      drag: this.handleDrag.bind(this),
      click: this.handleMouseClick.bind(this),
      keyup: this.handleKeyUp.bind(this),
      keydown: this.handleKeyDown.bind(this),
      keypress: this.handleKeyPress.bind(this),
      copy: this.handleCopy.bind(this),
      paste: this.handlePaste.bind(this),
      cut: this.handleCut.bind(this),
      drop: this.handleDrop.bind(this),
      compositionstart: this.handleCompositionStart.bind(this),
      compositionupdate: this.handleCompositionUpdate.bind(this),
      compositionend: this.handleCompositionEnd.bind(this),
      input: this.handleInput.bind(this),
      beforeinput: this.handleBeforeInput.bind(this),
      focus: this.handleFocus.bind(this),
      focusin: this.handleFocus.bind(this),
      focusout: this.handleFocus.bind(this),
    } as const;

    this.editorRootListeners = {
      contextmenu: this.handleContextMenu.bind(this),
      wheel: this.handleMouseWheel.bind(this),
    } as const;

    this.documentListeners = {
      keydown: this.handleDocumentKeyDown.bind(this),
      mousemove: this.handleDocumentMouseMove.bind(this),
      mouseup: this.handleDocumentMouseUp.bind(this),
      paste: this.handleDocumentPaste.bind(this),
      selectionchange: this.handleSelectionChange.bind(this),
    } as const;
  }

  static getInstance(): EventsManager {
    if (!EventsManager.instance) {
      EventsManager.instance = new EventsManager();
    }
    return EventsManager.instance;
  }

  protected showDebugMessage(message: string, ...args: any[]) {
    if (this.DEBUG) {
      logger.debug(message, ...args);
    }
  }

  startEventsManager(documentContainer?: HTMLElement) {
    const editorRoot = document.getElementById('EditorRoot');
    if (documentContainer) {
      this.documentContainer = documentContainer;
      Object.typedKeys(this.pageListeners).forEach((event) => {
        //@ts-expect-error listeners typed mapping
        this.documentContainer?.addEventListener(event, this.pageListeners[event]);
      });
    }
    if (editorRoot) {
      Object.typedKeys(this.editorRootListeners).forEach((event) => {
        //@ts-expect-error listeners typed mapping
        editorRoot.addEventListener(event, this.editorRootListeners[event]);
      });
    }

    Object.typedKeys(this.documentListeners).forEach((event) => {
      //@ts-expect-error listeners typed mapping
      document.addEventListener(event, this.documentListeners[event]);
    });
  }

  stopEventsManager() {
    const editorRoot = document.getElementById('EditorRoot');
    if (this.documentContainer) {
      const keys = Object.typedKeys(this.pageListeners);
      if (keys.length) {
        keys.forEach((event) => {
          //@ts-expect-error listeners typed mapping
          this.documentContainer?.removeEventListener(event, this.pageListeners[event]);
        });
      }
      this.documentContainer = undefined;
    }
    if (editorRoot) {
      const keys = Object.typedKeys(this.editorRootListeners);
      if (keys.length) {
        keys.forEach((event) => {
          //@ts-expect-error listeners typed mapping
          editorRoot.removeEventListener(event, this.editorRootListeners[event]);
        });
      }
    }

    const docKeys = Object.typedKeys(this.documentListeners);
    if (docKeys.length) {
      docKeys.forEach((event) => {
        //@ts-expect-error listeners typed mapping
        document.removeEventListener(event, this.documentListeners[event]);
      });
    }

    this.removeAllEventEmitterListeners();
  }

  destroy() {
    this.stopEventsManager();
  }

  remove(
    event: Editor.Events.EventNames,
    listener: Editor.Events.EventTypes[Editor.Events.EventNames],
  ) {
    if (listener) {
      this.removeListener(event, listener);
    }
  }

  handleFocus(event: FocusEvent) {
    this.showDebugMessage('EventsManager handleFocus', event);
    this.emit('FOCUS', event);
  }

  handleDrag(event: DragEvent) {
    this.showDebugMessage('EventsManager handleDrag', event);
  }

  handleMouseDown(event: MouseEvent) {
    this.showDebugMessage('EventsManager handleMouseDown', event);

    this.mousedownTarget = event.target as EventTarget;
    this.emit('MOUSE_DOWN', event);
  }

  handleMouseOver(event: MouseEvent) {
    this.emit('MOUSE_OVER', { event, mousedownTarget: this.mousedownTarget });
  }

  handleMouseUp(event: MouseEvent) {
    this.showDebugMessage('EventsManager handleMouseUp', event, this.mousedownTarget);

    this.emit('MOUSE_UP', { event, mousedownTarget: this.mousedownTarget });
  }

  handleMouseClick(event: MouseEvent) {
    this.showDebugMessage('EventsManager handleMouseClick', event);

    this.emit('click', event);
  }

  handleMouseWheel(event: WheelEvent) {
    this.emit('mousewheel DOMMouseScroll', event);
  }

  handleKeyUp(event: KeyboardEvent) {
    this.emit('KEY_UP', event);
  }

  handleKeyDown(event: KeyboardEvent) {
    this.emit('KEY_DOWN', event);
  }

  handleKeyPress(event: KeyboardEvent) {
    this.emit('KEY_PRESS', event);
  }

  handleContextMenu(event: MouseEvent) {
    this.emit('contextmenu', event);
  }

  //ClipBoard Events
  handleCopy(event: ClipboardEvent) {
    this.showDebugMessage('EventsManager handleCopy', event);
    this.emit('COPY', event);
  }

  handleCut(event: ClipboardEvent) {
    this.showDebugMessage('EventsManager handleCut', event);
    this.emit('CUT', event);
  }

  handlePaste(event: ClipboardEvent) {
    this.showDebugMessage('EventsManager handlePaste', event);
    this.emit('PASTE', event);
  }

  handleDrop(event: DragEvent) {
    this.showDebugMessage('EventsManager handleDrop', event);
    this.emit('DROP', event);
  }

  handleBeforeInput(event: InputEvent) {
    this.showDebugMessage('EventsManager handleBeforeInput', event);
    this.emit('BEFORE_INPUT', event);
  }

  handleInput(event: Event) {
    this.showDebugMessage('EventsManager handleInput', event);
    this.emit('INPUT', event);
  }

  handleCompositionStart(event: CompositionEvent) {
    this.showDebugMessage('EventsManager handleCompositionStart', event);
    this.emit('COMPOSITION_START', event);
  }

  handleCompositionUpdate(event: CompositionEvent) {
    this.showDebugMessage('EventsManager handleCompositionUpdate', event);
    this.emit('COMPOSITION_UPDATE', event);
  }

  handleCompositionEnd(event: CompositionEvent) {
    this.showDebugMessage('EventsManager handleCompositionEnd', event);
    this.emit('COMPOSITION_END', event);
  }

  //Document Related Events
  handleSelectionChange(event: Event) {
    this.emit('DOCUMENT_SELECTION_CHANGE', event);
  }

  handleDocumentMouseMove(event: MouseEvent) {
    this.emit('DOCUMENT_MOUSE_MOVE', event);
  }

  handleDocumentMouseUp(event: MouseEvent) {
    this.emit('DOCUMENT_MOUSE_UP', event);
  }

  handleDocumentPaste(event: ClipboardEvent) {
    this.emit('DOCUMENT_PASTE', event);
  }

  handleDocumentKeyDown(event: KeyboardEvent) {
    const pageContainer = EditorDOMUtils.getContentContainer();

    let selection;

    try {
      selection = window.getSelection();
      if (selection != null && pageContainer != null) {
        // Firefox keeps the editor selection while an outside input is focused, so ignore those keys
        if (selection.type !== 'None' && event.target instanceof HTMLElement && event.target.hasAttribute('ispagenode')) {
          const range = selection.getRangeAt(0);

          if (pageContainer.contains(range.commonAncestorContainer)) {
            this.handleKeyDown(event);
          }
        }
      }

      this.emit('DOCUMENT_KEY_DOWN', event);
    } catch (error) {
      Logger.captureException(error);
    }
  }
}

export default EventsManager;
